import { useState, useCallback, useEffect, useRef } from 'react';
import { getTodayPackageOrders, updatePackageOrderStatus, PackageOrderDto } from '../../../../services/salesService';
import { PackageOrder, OrderSource, OrderStatus, PaymentType, MasaSiparisi, MasaSiparisDetay } from '../types';
import { mainApi } from '../../../../config/apiClient';
import { isBusinessDayDate } from '../utils/helpers';

// ============================================================================
// USE ORDERS HOOK - PAKET SİPARİŞ YÖNETİMİ
// ============================================================================

export interface UseOrdersOptions {
  companyId: string | null;
  branchId?: string | null;
  /** Otomatik yenileme aralığı (ms). Varsayılan 60000 (1 dakika). 0 = devre dışı */
  autoRefreshInterval?: number;
  /** SignalR hook'undan gelen QR siparişleri için setter */
  setQrMenuOrders?: React.Dispatch<React.SetStateAction<MasaSiparisi[]>>;
  /** SignalR hook'undan gelen E-Commerce siparişleri için setter */
  setEcommerceOrders?: React.Dispatch<React.SetStateAction<MasaSiparisi[]>>;
  /** SignalR hook'undan gelen Kiosk siparişleri için setter */
  setKioskOrders?: React.Dispatch<React.SetStateAction<MasaSiparisi[]>>;
  /** Siparişin bu şubeye ait olup olmadığını kontrol eden fonksiyon */
  isQrOrderForThisBranch?: (order: MasaSiparisi) => boolean;
}

export interface UseOrdersReturn {
  /** Paket siparişleri (telefon siparişleri) */
  orders: PackageOrder[];
  /** Geçmiş siparişler (müşteri filtresi için) */
  pastOrders: PackageOrder[];
  /** Yükleniyor durumu */
  loading: boolean;
  /** Siparişleri yükle */
  loadOrders: () => Promise<void>;
  /** MasaSiparisleri'ni yükle (QR/E-Commerce/Kiosk) */
  loadMasaSiparisleri: () => Promise<void>;
  /** MasaSiparisi durumunu güncelle */
  updateMasaSiparisiStatus: (orderId: number, newStatus: string) => Promise<boolean>;
  /** Sipariş listesini set et (dışarıdan yükleme için) */
  setOrders: React.Dispatch<React.SetStateAction<PackageOrder[]>>;
  /** Geçmiş siparişleri set et */
  setPastOrders: React.Dispatch<React.SetStateAction<PackageOrder[]>>;
}

/**
 * Sipariş durumunu API formatından UI formatına dönüştür
 * Akış: Yeni (PENDING/CONFIRMED) -> Hazır (READY) -> Kurye (ON_DELIVERY) -> Teslim Edildi/Edilemedi
 */
export const mapOrderStatus = (status: string): OrderStatus => {
  const statusMap: { [key: string]: OrderStatus } = {
    'PENDING': 'new',
    'CONFIRMED': 'new',
    'PREPARING': 'new', // Hazırlanıyor artık yok, Yeni olarak göster
    'READY': 'ready',
    'ON_DELIVERY': 'onway',
    'DELIVERED': 'delivered',
    'UNDELIVERED': 'undelivered',
    'CANCELLED': 'cancelled'
  };
  return statusMap[status] || 'new';
};

/**
 * Ödeme yöntemini UI formatına dönüştür
 */
export const mapPaymentType = (paymentMethod: string | undefined): PaymentType => {
  if (!paymentMethod) return 'cash'; // Varsayılan
  const paymentMap: { [key: string]: PaymentType } = {
    'CASH': 'cash',
    'POS': 'card',
    'ONLINE': 'online'
  };
  return paymentMap[paymentMethod.toUpperCase()] || 'cash';
};

/**
 * Paket sipariş yönetimi hook'u
 */
export const useOrders = (options: UseOrdersOptions): UseOrdersReturn => {
  const {
    companyId,
    branchId,
    autoRefreshInterval = 60000,
    setQrMenuOrders,
    setEcommerceOrders,
    setKioskOrders,
    isQrOrderForThisBranch,
  } = options;
  
  // Paket siparişleri (telefon siparişleri)
  const [orders, setOrders] = useState<PackageOrder[]>([]);
  const [pastOrders, setPastOrders] = useState<PackageOrder[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Otomatik yenileme interval ref
  const autoRefreshRef = useRef<NodeJS.Timeout | null>(null);
  
  // ============================================================================
  // PAKET SİPARİŞLERİNİ YÜKLE
  // ============================================================================
  
  const loadOrders = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      console.log('📦 [useOrders] Siparişler yükleniyor...', { companyId, branchId });
      
      const response = await getTodayPackageOrders(branchId || undefined);
      
      if (response.Success && response.Data) {
        // API'den gelen veriyi PackageOrder tipine dönüştür
        const mappedOrders: PackageOrder[] = response.Data.map((order: PackageOrderDto) => {
          const status = mapOrderStatus(order.OrderStatus || 'PENDING');
          const createdAt = new Date(order.CreatedAt);
          
          // ✅ Bekleme süresi hesaplama:
          // - Teslim edilmiş siparişler için: CompletedAt - CreatedAt (sabit)
          // - Aktif siparişler için: Şu an - CreatedAt (dinamik)
          let waitingMinutes = order.WaitingMinutes || 0;
          if (status === 'delivered' || status === 'cancelled' || status === 'undelivered') {
            // Teslim edildi, iptal edildi veya teslim edilemedi - bekleme süresi sabit
            if (order.CompletedAt) {
              const completedAt = new Date(order.CompletedAt);
              waitingMinutes = Math.floor((completedAt.getTime() - createdAt.getTime()) / 60000);
            }
          }
          
          return {
            id: order.Id,
            shortOrderNo: order.ShortCode || '',
            externalOrderNo: order.OrderNumber || undefined,
            source: 'phone' as OrderSource, // Varsayılan olarak telefon
            customerName: order.AccountName || '',
            customerPhone: order.CustomerPhone || '',
            customerAddress: order.CustomerAddress || '',
            orderDate: createdAt.toISOString().split('T')[0],
            orderTime: createdAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
            totalAmount: order.TotalAmount || 0,
            status: status,
            itemCount: order.ItemCount || order.Items?.length || 0,
            waitingMinutes: waitingMinutes,
            paymentType: mapPaymentType(order.PaymentMethod),
            courierName: order.CourierName || undefined,
            courierStatus: order.CourierStatus || undefined,
            isPaid: order.PaymentStatus === 'PAID',
            totalPaid: order.TotalPaid || 0,
            paymentMethod: order.PaymentMethod || undefined,
            // ✅ YENİ: Tarih bilgileri
            createdAt: order.CreatedAt,
            completedAt: order.CompletedAt || undefined,
          };
        });
        
        console.log('✅ [useOrders] Siparişler yüklendi:', mappedOrders.length);
        setOrders(mappedOrders);
      } else {
        console.warn('⚠️ [useOrders] Sipariş yüklenemedi:', response.Message);
      }
    } catch (error) {
      console.error('❌ [useOrders] Sipariş yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId]);
  
  // ============================================================================
  // MASA SİPARİŞLERİNİ YÜKLE (QR / E-COMMERCE / KIOSK)
  // ============================================================================
  
  const loadMasaSiparisleri = useCallback(async () => {
    if (!companyId) return;
    if (!setQrMenuOrders && !setEcommerceOrders && !setKioskOrders) {
      console.warn('⚠️ [useOrders] MasaSiparisleri yüklemek için setter\'lar gerekli');
      return;
    }
    
    try {
      const { data: result } = await mainApi.get(`/api/MasaSiparisi/orders`);
      if (result.success && result.data) {
        const allOrders: MasaSiparisi[] = result.data.map((order: any) => {
          let siparisDetaylari: MasaSiparisDetay[] = [];
          try {
            if (typeof order.siparisDetaylari === 'string') {
              const parsed = JSON.parse(order.siparisDetaylari || '[]');
              siparisDetaylari = parsed.siparisDetaylari || parsed || [];
            } else if (order.siparisDetaylari) {
              siparisDetaylari = order.siparisDetaylari.siparisDetaylari || order.siparisDetaylari || [];
            }
          } catch (e) {
            console.error('Sipariş detayları parse hatası:', e);
          }
          return {
            id: order.id,
            siparisId: order.siparisId,
            masaNumarasi: order.masaNumarasi || order.masaId,
            masaAdi: order.masaAdi,
            kullaniciAdi: order.kullaniciAdi,
            siparisDurumu: order.siparisDurumu,
            siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
            siparisGuncellenmeTarihi: order.siparisGuncellenmeTarihi,
            toplamTutar: order.toplamTutar,
            qrMenuId: order.qrMenuId,
            siparisDetaylari: siparisDetaylari,
            siparisNotu: order.siparisNotu,
            siparisKaynagi: order.siparisKaynagi,
            musteriAdi: order.musteriAdi || order.kullaniciAdi,
            musteriTelefon: order.musteriTelefon,
            musteriAdres: order.musteriAdres,
          };
        });
        const todayOrders = allOrders.filter(order => {
          const orderDate = order.siparisOlusturmaTarihi?.split('T')[0];
          if (!orderDate || !isBusinessDayDate(orderDate)) return false;
          const durum = order.siparisDurumu?.toLowerCase() || '';
          return durum !== 'iptal' && durum !== 'cancelled';
        });
        const qrOrders: MasaSiparisi[] = [];
        const ecomOrders: MasaSiparisi[] = [];
        const kioskOrdrs: MasaSiparisi[] = [];
        todayOrders.forEach(order => {
          let kaynak = (order.siparisKaynagi || '').toLowerCase();
          if (!kaynak && order.siparisId) {
            if (order.siparisId.startsWith('D-') || order.siparisId.startsWith('P-') || /[DP]-\d+$/.test(order.siparisId)) {
              kaynak = 'kiosk';
            } else {
              const siparisIdParts = order.siparisId.split('-');
              if (siparisIdParts.length >= 2) {
                const sourceType = siparisIdParts[siparisIdParts.length - 2].toLowerCase();
                if (sourceType === 'qr') kaynak = 'qrmenu';
                else if (sourceType === 'kiosk') kaynak = 'kiosk';
                else if (sourceType === 'ecom' || sourceType === 'ecommerce') kaynak = 'ecommerce';
              }
            }
          }
          console.log('📦 [useOrders] Sipariş kaynağı:', order.siparisId, '→', kaynak || 'UNKNOWN');
          if (kaynak.includes('ecommerce') || kaynak.includes('e-commerce') || kaynak.includes('ecom')) {
            ecomOrders.push(order);
          } else if (kaynak.includes('kiosk')) {
            kioskOrdrs.push(order);
          } else if (kaynak.includes('qr') || kaynak.includes('qrmenu')) {
            qrOrders.push(order);
          }
        });
        const filterFn = isQrOrderForThisBranch || (() => true);
        const filteredQrOrders = qrOrders.filter(filterFn);
        const filteredEcomOrders = ecomOrders.filter(filterFn);
        const filteredKioskOrders = kioskOrdrs.filter(filterFn);
        console.log('📦 [useOrders] MasaSiparisleri yüklendi:', {
          qr: `${filteredQrOrders.length}/${qrOrders.length}`,
          ecommerce: `${filteredEcomOrders.length}/${ecomOrders.length}`,
          kiosk: `${filteredKioskOrders.length}/${kioskOrdrs.length}`,
          branchFilter: branchId || 'YOK (tüm şubeler)'
        });
        setQrMenuOrders?.(filteredQrOrders);
        setEcommerceOrders?.(filteredEcomOrders);
        setKioskOrders?.(filteredKioskOrders);
      }
    } catch (error) {
      console.error('❌ [useOrders] MasaSiparisleri yükleme hatası:', error);
    }
  }, [companyId, branchId, setQrMenuOrders, setEcommerceOrders, setKioskOrders, isQrOrderForThisBranch]);
  
  // ============================================================================
  // MASA SİPARİŞİ DURUMU GÜNCELLE
  // ============================================================================
  
  const updateMasaSiparisiStatus = useCallback(async (orderId: number, newStatus: string): Promise<boolean> => {
    if (!companyId) {
      console.error('❌ [useOrders] CompanyId yok');
      return false;
    }
    
    try {
      console.log('🔄 [useOrders] MasaSiparisi durumu güncelleniyor:', orderId, '->', newStatus);
      const { data: result } = await mainApi.post(`/api/MasaSiparisi/order/${orderId}/status`, {
        siparisDurumu: newStatus,
      });
      if (result.success) {
        console.log('✅ [useOrders] MasaSiparisi durumu güncellendi:', orderId, '->', newStatus);
        const updateOrder = (prev: MasaSiparisi[]) => prev.map(order => 
          order.id === orderId
            ? { ...order, siparisDurumu: newStatus, siparisGuncellenmeTarihi: new Date().toISOString() }
            : order
        );
        setQrMenuOrders?.(updateOrder);
        setEcommerceOrders?.(updateOrder);
        setKioskOrders?.(updateOrder);
        return true;
      }
      console.error('❌ [useOrders] MasaSiparisi durumu güncelleme hatası');
      return false;
    } catch (error) {
      console.error('❌ [useOrders] MasaSiparisi durumu güncelleme hatası:', error);
      return false;
    }
  }, [companyId, setQrMenuOrders, setEcommerceOrders, setKioskOrders]);
  
  // ============================================================================
  // OTOMATİK YENİLEME
  // ============================================================================
  
  useEffect(() => {
    if (autoRefreshInterval > 0 && companyId) {
      autoRefreshRef.current = setInterval(() => {
        console.log('🔄 [useOrders] Otomatik yenileme...');
        loadOrders();
        loadMasaSiparisleri();
      }, autoRefreshInterval);
      
      return () => {
        if (autoRefreshRef.current) {
          clearInterval(autoRefreshRef.current);
        }
      };
    }
  }, [autoRefreshInterval, companyId, loadOrders, loadMasaSiparisleri]);
  
  return {
    orders,
    pastOrders,
    loading,
    loadOrders,
    loadMasaSiparisleri,
    updateMasaSiparisiStatus,
    setOrders,
    setPastOrders,
  };
};

export default useOrders;
